import React from 'react'

import { Tooltip as GluestackTooltip } from '../../_gluestack/tooltip'
import { useDismissOnOutsideScroll } from '../../useDismissOnOutsideScroll'

type GluestackTooltipProps = React.ComponentProps<typeof GluestackTooltip>
type TriggerRenderer = GluestackTooltipProps['trigger']
type TriggerProps = Parameters<TriggerRenderer>[0] & { ref?: React.Ref<unknown> }

/**
 * Gluestack ships `useCloseOnScroll`, but `TooltipContent` never passes `onClose`
 * into `useOverlayPosition`, so the listener is inert and an open tooltip stays
 * pinned at its original coordinates while the anchor scrolls away. Owning
 * `isOpen` here is what makes an outside dismissal possible at all.
 */
export const Tooltip = React.forwardRef<
    React.ComponentRef<typeof GluestackTooltip>,
    GluestackTooltipProps
>(function Tooltip({ isOpen, onOpen, onClose, defaultIsOpen, trigger, ...props }, ref) {
    const triggerRef = React.useRef<unknown>(null)
    const [isVisible, setIsVisible] = React.useState(defaultIsOpen ?? false)
    const open = isOpen ?? isVisible

    const handleOpen = React.useCallback(() => {
        setIsVisible(true)
        onOpen?.()
    }, [onOpen])

    const handleClose = React.useCallback(() => {
        setIsVisible(false)
        onClose?.()
    }, [onClose])

    useDismissOnOutsideScroll(open, triggerRef, handleClose)

    const renderTrigger = React.useCallback<TriggerRenderer>(
        (triggerProps, state) => {
            const { ref: gluestackRef, ...rest } = triggerProps as TriggerProps
            const setNode = (node: unknown) => {
                triggerRef.current = node
                if (typeof gluestackRef === 'function') {
                    gluestackRef(node as never)
                } else if (gluestackRef) {
                    ;(gluestackRef as React.MutableRefObject<unknown>).current = node
                }
            }
            return trigger({ ...rest, ref: setNode } as TriggerProps, state)
        },
        [trigger],
    )

    return (
        <GluestackTooltip
            ref={ref}
            {...props}
            trigger={renderTrigger}
            isOpen={open}
            onOpen={handleOpen}
            onClose={handleClose}
        />
    )
})

Tooltip.displayName = 'Tooltip'
